import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import HelpCenter from "./help-center";
import { MACRO_KEY } from "../../../optics/defaults";
import {
  EDITOR_MACROS,
  HELP_CENTER,
  MACRO,
  MACRO_CATEGORIES,
} from "./defaults";
import {
  SidebarBottomItemsContainer,
  SidebarCollapsibleContainer,
  SidebarCollapsibleHeading,
  SidebarContainer,
  SidebarItemContainer,
  SidebarItemLabel,
  SidebarItemsContainer,
} from "./styles";
import { useOpticsStore } from "../../../stores/use-optics-store";
import { macroStores } from "../../../stores/export-stores";

const Sidebar = () => {
  const { t } = useTranslation();
  const [isHelpCenterOpen, setIsHelpCenterOpen] = useState();
  const activeItemRef = useRef(null);
  const hasScrolledInitially = useRef(false);

  const macros = MACRO(t);
  const editorMacros = EDITOR_MACROS(t);

  const { macro, setMacro, setDrawers, setAdvancedMacroEmptyState } =
    useOpticsStore((state) => ({
      macro: state.macro,
      setMacro: state.setMacro,
      setDrawers: state.setDrawers,
      setAdvancedMacroEmptyState: state.setAdvancedMacroEmptyState,
    }));

  const macroData = macroStores[macro]?.((state) => state?.data || {});

  const macroHasData = Array.isArray(macroData)
    ? !!macroData.length
    : !!Object.keys(macroData).length;

  useEffect(() => {
    if (macroHasData) {
      setDrawers({ templates: false, settings: true });
    } else {
      setDrawers({ templates: true, settings: false });
    }
  }, [macro, macroHasData, setDrawers]);

  useEffect(() => {
    if (hasScrolledInitially.current) return;

    const timer = setTimeout(() => {
      if (activeItemRef.current) {
        activeItemRef.current.scrollIntoView({
          behavior: "auto",
          block: "nearest",
        });
        hasScrolledInitially.current = true;
      }
    }, 30);

    return () => clearTimeout(timer);
  }, [macro]);

  const macrosGrouped = Object.groupBy(
    editorMacros,
    (macro) => macro?.category ?? MACRO_CATEGORIES.UNCATEGORIZED
  );

  const OverviewIcon = macros[MACRO_KEY.OVERVIEW_KEY].Icon;

  return (
    <SidebarContainer id="optics-sidebar">
      <SidebarItemsContainer>
        <SidebarItemContainer
          isActive={macros[MACRO_KEY.OVERVIEW_KEY].key === macro}
          onClick={() => {
            if (macro === macros[MACRO_KEY.OVERVIEW_KEY].key) return;

            if (
              macros[MACRO_KEY.OVERVIEW_KEY].key === MACRO_KEY.OTHER_MODULES_KEY
            ) {
              setDrawers({ templates: false, settings: false });
              setAdvancedMacroEmptyState(true);
              setMacro({ key: macros[MACRO_KEY.OVERVIEW_KEY].key });
            } else {
              setMacro({ key: macros[MACRO_KEY.OVERVIEW_KEY].key });
              setAdvancedMacroEmptyState(false);
            }
          }}
        >
          <OverviewIcon />
          <SidebarItemLabel>
            {macros[MACRO_KEY.OVERVIEW_KEY].label}
          </SidebarItemLabel>
        </SidebarItemContainer>

        {Object.entries(macrosGrouped)
          .filter(([category]) => category !== MACRO_CATEGORIES.UNCATEGORIZED)
          .map(([category, macros]) => (
            <SidebarCollapsibleContainer key={category} open>
              <SidebarCollapsibleHeading>
                {t("macro-category." + category)}
              </SidebarCollapsibleHeading>

              {macros.map(({ id, label, Icon, key }) => (
                <SidebarItemContainer
                  ref={macro === key ? activeItemRef : null}
                  key={id}
                  isActive={key === macro}
                  onClick={() => {
                    if (macro === key) return;

                    if (key === MACRO_KEY.OTHER_MODULES_KEY) {
                      setDrawers({ templates: false, settings: false });
                      setAdvancedMacroEmptyState(true);
                      setMacro({ key });
                    } else {
                      setMacro({ key });
                      setAdvancedMacroEmptyState(false);
                    }
                  }}
                >
                  <Icon />
                  <SidebarItemLabel>{label}</SidebarItemLabel>
                </SidebarItemContainer>
              ))}
            </SidebarCollapsibleContainer>
          ))}
      </SidebarItemsContainer>

      <SidebarBottomItemsContainer>
        <SidebarItemContainer
          style={{ position: "relative" }}
          onClick={() => setIsHelpCenterOpen(true)}
        >
          <HELP_CENTER.Icon />
          <SidebarItemLabel>{HELP_CENTER.label}</SidebarItemLabel>
        </SidebarItemContainer>

        <HelpCenter
          isHelpCenterOpen={isHelpCenterOpen}
          setIsHelpCenterOpen={setIsHelpCenterOpen}
        />
      </SidebarBottomItemsContainer>
    </SidebarContainer>
  );
};

export default Sidebar;
